import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, LinearProgress, CircularProgress, Fade } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";


const EditorLoading = ({
    msg = "Estamos reformulando la respuesta, por favor espera…",
    steps,
    intervalMs = 1100,
    loopTips,
    fullOverlay = false,
}) => {
    const defaultSteps = useMemo(
        () =>
            steps?.length
                ? steps
                : [
                    "Analizando el contexto actual",
                    "Detectando sección a modificar",
                    "Generando propuesta de mejora",
                    "Ajustando tono y coherencia",
                    "Aplicando cambios al documento",
                    "Limpiando formato y estilos",
                    "Actualizando vista previa",
                ],
        [steps]
    );

    const defaultTips = useMemo(
        () =>
            loopTips?.length
                ? loopTips
                : [
                    "Consejo: puedes deshacer con CTRL/⌘+Z si no te convence el resultado.",
                    "Sugerencia: usa ‘Formalizar’ o ‘Resumir’ sobre texto seleccionado.",
                    "Tip: combina ‘Mejorar redacción’ + ‘Acortar’ para textos directos.",
                    "Info: el estilo se alinea con el contexto actual del editor.",
                ],
        [loopTips]
    );

    const [activeStep, setActiveStep] = useState(0);
    const [tipIndex, setTipIndex] = useState(0);

    useEffect(() => {
        const id = setInterval(() => {
            setActiveStep((s) => Math.min(s + 1, defaultSteps.length));
        }, intervalMs);
        return () => clearInterval(id);
    }, [defaultSteps.length, intervalMs]);

    useEffect(() => {
        const id = setInterval(() => {
            setTipIndex((i) => (i + 1) % defaultTips.length);
        }, 5000);
        return () => clearInterval(id);
    }, [defaultTips.length]);



    return (
        <Box
            role="status"
            aria-live="polite"
            sx={{
                position: fullOverlay ? "absolute" : "relative",
                inset: fullOverlay ? 0 : "auto",
                zIndex: fullOverlay ? 10 : "auto",
                display: "flex",
                alignItems: "top",
                justifyContent: "center",
                width: "100%",
                minHeight: fullOverlay ? "100%" : 360,
                height: fullOverlay ? "100%" : "auto",
                bgcolor: fullOverlay ? "rgba(255, 255, 255, 1)" : "transparent",
                backdropFilter: fullOverlay ? "blur(1px)" : "none",
                fontFamily: "Montserrat",
                pointerEvents: fullOverlay ? "auto" : "none",
            }}
        >
            {/* Barra superior indeterminada */}
            {fullOverlay && (
                <LinearProgress
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        "& .MuiLinearProgress-bar": { backgroundColor: "#336B87" },
                    }}
                />
            )}

            {/* Contenido centrado */}
            <Box
                sx={{
                    width: "100%",
                    maxWidth: 640,
                    px: { xs: 2, sm: 3 },
                    boxSizing: "border-box",
                    textAlign: "center",
                    mt: 5
                }}
            >
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 0.5 }}>
                    <CircularProgress size={24} thickness={5} sx={{ color: "#336B87" }} />
                    <Typography sx={{ fontWeight: 700, color: "#336B87", fontSize: { xs: 14, sm: 16 }, fontFamily: "Montserrat" }}>
                        UmmIA está trabajando en tu documento…
                    </Typography>
                </Box>

                {/* Subtítulo */}
                <Typography sx={{ color: "#5f6b75", fontSize: { xs: 12, sm: 13 }, mb: 2, fontFamily: "Montserrat" }}>
                    {msg}
                </Typography>

                {/* Pasos 
        <Box
          sx={{
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 0.5,
            textAlign: "left",
            mx: "auto",
            px: { xs: 1, sm: 2 },
            maxHeight: 240,
            overflow: "auto",
          }}
        >
          {defaultSteps.map((label, idx) => {
            const isDone = idx < activeStep;
            const isCurrent = idx === activeStep;
            return (
              <Box key={label + idx} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}>
                {isDone ? (
                  <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#1FC16B" }} />
                ) : (
                  <HourglassEmptyIcon sx={{ fontSize: 18, color: "#A7B3BC" }} />
                )}
                <Typography
                  sx={{
                    fontSize: 13,
                    color: isDone ? "#2C3E50" : "#6B7A86",
                    fontWeight: isCurrent ? 600 : 500,
                  }}
                >
                  {label}
                  {isCurrent && "…"}
                </Typography>
              </Box>
            );
          })}
        </Box>*/}

                {/* Tip que rota */}
                <Fade in timeout={400} key={tipIndex}>
                    <Box sx={{ mt: 2 }}>
                        <Typography
                            sx={{
                                color: "#1D3D4D",
                                fontSize: { xs: 12, sm: 13 },
                                fontWeight: 500,
                                fontFamily: "Montserrat"
                            }}
                        >
                            {defaultTips[tipIndex]}
                        </Typography>
                    </Box>
                </Fade>
            </Box>
        </Box>
    );
};

export default EditorLoading;
